import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@kcftech/design-system';
import { DebouncedSearchInput } from 'components/data/DebouncedSearchInput';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { CRUDTableActions } from 'components/table/CRUDTableActions';
import { PagedDataSource } from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { ReactNode, useEffect, useMemo, useState } from 'react';

export type CRUDTableFilter = {
    nameSearch?: string;
};

export type CRUDTableProps<TData, TFilter> = Readonly<{
    /**
     * Class applied around the table container
     */
    className?: string;
    /**
     * Column configuration.
     */
    columns: TableColumn<TData>[];
    /**
     * Create button text. Defaults to Create
     */
    createButtonText?: string;
    /**
     * Renders a custom action item column
     */
    customActionItems?: (record: TData) => ReactNode;
    /**
     * Data source to apply to table
     */
    dataSource: PagedDataSource<TData, TFilter>;
    /**
     * Callback for when the create button is clicked
     */
    onCreate?: () => void;
    /**
     * Callback for when the delete button is clicked
     */
    onDelete?: (record: TData) => void;
    /**
     * Callback for when the edit pencil is clicked
     * If not supplied edit is hidden
     */
    onEdit?: (record: TData) => void;
    /**
     * Used to generate a unique identifier for each row.
     * Also used for the aria-label of the expand/collapse button
     * in compact mode.
     */
    rowKey: (rowData: TData) => string;
    /**
     * description of data row to be used in the action button title text.
     * Example: `titleDescription="Corporation"` will result in
     * "Edit Corporation"/"Delete Corporation" when hovering over those action buttons.
     */
    titleDescription?: string;
    /**
     * No result message to display when results are empty
     */
    noResultMessage: string;
}>;

export function CRUDTable<TData, TFilter extends CRUDTableFilter>({
    className,
    columns,
    createButtonText = 'Create',
    customActionItems,
    dataSource,
    onCreate,
    onDelete,
    onEdit,
    rowKey,
    titleDescription = '',
    noResultMessage,
}: CRUDTableProps<TData, TFilter>) {
    const { setFilter } = dataSource;
    const [search, setSearch] = useState('');

    useEffect(() => {
        setFilter((filter) => ({
            ...filter,
            nameSearch: search,
        }));
    }, [setFilter, search]);

    const mappedColumns = useMemo<TableColumn<TData>[]>(() => {
        if (customActionItems || onDelete || onEdit) {
            return [
                ...columns,
                {
                    header: () => <span aria-label="actions">Actions</span>,
                    id: 'action',
                    value: ({ rowData }) => (
                        <CRUDTableActions
                            customActionItems={customActionItems}
                            onDelete={onDelete}
                            onEdit={onEdit}
                            titleDescription={titleDescription}
                            rowData={rowData}
                        />
                    ),
                    locked: true,
                },
            ];
        }
        return columns;
    }, [columns, customActionItems, onDelete, onEdit, titleDescription]);

    return (
        <div className={`flex-column flex-grow-1 full-height ${className ?? ''}`}>
            <span className="flex justify-between pt-5 pb-15">
                <DebouncedSearchInput
                    value={search}
                    placeholder="Search"
                    onChange={setSearch}
                />
                {onCreate && (
                    <Button
                        appearance="primary"
                        spacing="medium"
                        onClick={onCreate}
                        icon={<FontAwesomeIcon icon={faPlus} />}
                    >
                        {createButtonText}
                    </Button>
                )}
            </span>
            <DataSourceTable<TData, TFilter>
                dataSource={dataSource}
                columns={mappedColumns}
                rowKey={rowKey}
                noResultMessage={noResultMessage}
            />
        </div>
    );
}
